import {Component, inject, viewChild, signal, ChangeDetectionStrategy} from "@angular/core"
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog"
import {MatSnackBar} from "@angular/material/snack-bar"
import {MatTable, MatTableModule} from "@angular/material/table"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ActivatedRoute, Router} from "@angular/router"
import {TypedMatCellDefDirective} from "@app/common/directives"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {MaterialRevisionsHistoryDialogMaterialRevisionFragment} from "@app/platform/components/materials/material-revisions-history-dialog/material-revisions-history-dialog.generated"
import {IsNonNull} from "@cm/utils/filter"
import {DialogComponent} from "@common/components/dialogs/dialog/dialog.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {FormatDatePipe} from "@common/pipes/format-date/format-date.pipe"
import {TimeAgoPipe} from "@common/pipes/time-ago/time-ago.pipe"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {ContentTypeModel} from "@generated"
import {
    MaterialRevisionsHistoryDialogDeleteMaterialRevisionGQL,
    MaterialRevisionsHistoryDialogMaterialRevisionGQL,
} from "@platform/components/materials/material-revisions-history-dialog/material-revisions-history-dialog.generated"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-material-revisions-history-dialog",
    templateUrl: "./material-revisions-history-dialog.component.html",
    styleUrls: ["./material-revisions-history-dialog.component.scss"],
    imports: [MatTableModule, MatTooltipModule, TimeAgoPipe, FormatDatePipe, TypedMatCellDefDirective],
})
export class MaterialRevisionsHistoryDialogComponent {
    readonly $revisionTable = viewChild.required<MatTable<MaterialRevisionsHistoryDialogMaterialRevisionFragment>>("revisionTableRef")
    displayedColumns = ["revision", "author", "date", "corona", "cycles", "graph-schema", "comment"]

    readonly $material = signal({revisions: [] as MaterialRevisionsHistoryDialogMaterialRevisionFragment[]})
    readonly notifications = inject(NotificationsService)
    readonly refresh = inject(RefreshService)
    readonly route = inject(ActivatedRoute)
    readonly router = inject(Router)

    readonly materialRevisionGql = inject(MaterialRevisionsHistoryDialogMaterialRevisionGQL)
    readonly deleteMaterialRevisionGql = inject(MaterialRevisionsHistoryDialogDeleteMaterialRevisionGQL)
    readonly dialogRef = inject(MatDialogRef<MaterialRevisionsHistoryDialogComponent>)
    readonly data = inject<{material: {id: string}}>(MAT_DIALOG_DATA)
    private readonly dialog = inject(MatDialog)
    private readonly snackBar = inject(MatSnackBar)

    constructor() {
        void this.fetchRevisions()
    }

    async fetchRevisions() {
        const result = await fetchThrowingErrors(this.materialRevisionGql)({
            materialId: this.data.material.id,
        })
        this.$material.set({revisions: result.materialRevisions.filter(IsNonNull)})
    }

    getMatFileUrl(revision: MaterialRevisionsHistoryDialogMaterialRevisionFragment): string {
        if (!revision.matFileDataObject) throw Error("No matFileDataObject")
        return revision.matFileDataObject.downloadUrl
    }

    openRevision(revision: MaterialRevisionsHistoryDialogMaterialRevisionFragment) {
        this.router.navigate(["edit", revision.id], {
            relativeTo: this.route,
            queryParamsHandling: "preserve",
        })
        this.dialogRef.close()
    }

    deleteRevision(revision: MaterialRevisionsHistoryDialogMaterialRevisionFragment) {
        const dialogRef = this.dialog.open(DialogComponent, {
            disableClose: false,
            width: "400px",
            data: {
                title: "Delete revision",
                message: "The material revision will be deleted. " + "This action <strong>cannot be undone</strong>.<br><br>Are you sure you want to continue?",
                confirmLabel: "Delete revision",
                cancelLabel: "Cancel",
            },
        })
        dialogRef.afterClosed().subscribe(async (confirmed) => {
            if (!confirmed) {
                return
            }
            await this.notifications.withUserFeedback(
                async () => {
                    await mutateThrowingErrors(this.deleteMaterialRevisionGql)({
                        materialRevisionId: revision.id,
                    })
                    await this.fetchRevisions()
                    this.refresh.item({...this.data.material, __typename: ContentTypeModel.Material})
                    this.$revisionTable()?.renderRows()
                },
                {success: "Revision deleted successfully", error: "Cannot delete revision"},
            )
        })
    }
}
